import { ArrowLeft, Boxes, Terminal, AlertTriangle, ShieldAlert } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from "vite-react-ssg";
import { Card, CardContent } from "@/components/ui/card";
import NavigationButtons from "@/components/NavigationButtons";
import RelatedLinks from "@/components/RelatedLinks";
import LastUpdated from "@/components/LastUpdated";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const canonical = "https://deepseek.ai/blog/deepseek-harness-open-source-claude-code-alternative";
const PUBLISHED = "2026-08-16T09:00:00+00:00";

const modes = [
  {
    mode: "Standard",
    what:
      "Full coding agent: file editing, shell, file and web search, skills, planning, goals, subagents and workflows.",
  },
  {
    mode: "Code",
    what:
      "Exposes tools through the Code Mode SDK so the model can combine multi-step operations inside one TypeScript program.",
  },
  {
    mode: "Minimal",
    what: "A two-tool agent: persistent bash plus str_replace_editor. Nothing else in the loop.",
  },
  {
    mode: "Creator",
    what:
      "Inspect the live runtime, test Cordis plugins in memory, combine them into new modes and author presets.",
  },
];

const comparison = [
  {
    dimension: "Model choice",
    claude: "Optimised for Anthropic models; other providers need workarounds.",
    harness:
      "The model is a plugin. An OpenAI-compatible custom-provider form accepts DeepSeek, GLM, Kimi, Qwen, local Ollama or an Anthropic key.",
  },
  {
    dimension: "Session durability",
    claude: "Plugin failures or dependency changes typically require a restart.",
    harness:
      "Swapping or removing a component is designed not to tear down the session — the demo resumes mid-crash with context intact.",
  },
  {
    dimension: "Observability",
    claude: "Tracing usually needs an external tool such as LangSmith or Helicone.",
    harness:
      "Built-in Trajectory panel: per-turn context injection, tool calls, time-to-first-token, cache-hit rate and token counts.",
  },
  {
    dimension: "Agent surface",
    claude: "Fixed surface with slash commands and MCP glue you cannot restructure.",
    harness: "Four modes (Standard, Code, Minimal, Creator) plus preset authoring at runtime.",
  },
];

const plugins = [
  { name: "dsh-easy-ctx-manager", what: "Context management and trimming." },
  {
    name: "dsh-web-search-pro",
    what:
      "Multi-engine routing across DeepSeek, Exa, DDG, Bing and Jina, with SQLite caching and Playwright rendering.",
  },
  {
    name: "dsh-memory-vault",
    what: "Cross-session memory vault exposing memory_remember / memory_recall / memory_forget.",
  },
  {
    name: "dsh-cc-tui",
    what: "Claude Code-style fullscreen TUI with streaming expand and double-Esc rollback.",
  },
  {
    name: "task-passport",
    what:
      "Carries durable task state across DeepSeek Harness, WorkBuddy, Claude Code and Codex with machine-readable checkpoints.",
  },
];

const faqs = [
  {
    q: "What is DeepSeek Harness?",
    a: "DeepSeek Harness is an open-source, MIT-licensed coding agent runtime from DeepSeek (repo deepseek-harness, CLI dsh). It runs locally as a web app and lets you build with any model provider, in an architecture where every capability — models, tools, UI and the agent loop itself — is a hot-swappable plugin.",
  },
  {
    q: "How is DeepSeek Harness different from Claude Code?",
    a: "The difference is architectural. Claude Code is a fixed, closed harness optimised for Anthropic models. DeepSeek Harness is open source and model-agnostic, built on the Cordis plugin runtime, which is designed to add, remove or replace components at runtime without restarting the session.",
  },
  {
    q: "How do I install DeepSeek Harness?",
    a: "Install Node.js, then run `npx @deepseek-ai/dsh web`. The command starts the Web UI, served at http://127.0.0.1:3080 by default. You can also clone the GitHub repository and build from source with pnpm.",
  },
  {
    q: "Can I use non-DeepSeek models in DeepSeek Harness?",
    a: "Yes. The Models settings pane exposes a custom-provider form that accepts any OpenAI-compatible endpoint, so DeepSeek, GLM, Kimi, Qwen, a local Ollama server or an Anthropic key all work. The model is just another plugin.",
  },
  {
    q: "Is DeepSeek Harness free?",
    a: "The harness itself is free and MIT-licensed. You still pay whichever model provider you point it at. On DeepSeek's own API, V3.2 is $0.28 per 1M input tokens and $0.42 per 1M output tokens, with 90% off cached input tokens — but newer Pro-tier variants cost noticeably more per task, so re-run your own math before switching purely on price.",
  },
  {
    q: "Is DeepSeek Harness production-ready?",
    a: "No. It is in developer preview and iterating rapidly, with compatibility-breaking changes expected. There are also no independent SWE-bench-style comparisons yet against Claude Code, Codex or Cursor, and the plugin model carries a supply-chain risk: a community plugin has the same rights as a first-party one.",
  },
  {
    q: "What is Cordis?",
    a: "Cordis is the dependency-injection-style runtime underneath DeepSeek Harness, described in the paper 'A Programming Paradigm for Spatiotemporal Composability'. Its kernel manages plugin mounting, unmounting and dependencies. A plugin implements a Service and claims a stable key such as ctx.tools, ctx.llm or ctx.sessions, so plugins find each other by key instead of importing concrete implementations.",
  },
];

const DeepseekHarness = () => {
  const navigate = useNavigate();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline:
      "DeepSeek Harness Explained: The Open-Source, Plugin-First Alternative to Claude Code",
    datePublished: PUBLISHED,
    dateModified: PUBLISHED,
    author: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      url: "https://deepseek.ai",
    },
    publisher: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      logo: { "@type": "ImageObject", url: "https://deepseek.ai/og-image.png" },
    },
    description:
      "DeepSeek Harness is a free, MIT-licensed, plugin-first coding agent built on the Cordis runtime — an open-source Claude Code alternative where every model, tool and even the UI is hot-swappable. Install steps, architecture, comparison and honest limitations.",
    image: ["https://deepseek.ai/og-image.png"],
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://deepseek.ai/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://deepseek.ai/blog" },
      { "@type": "ListItem", position: 3, name: "DeepSeek Harness explained", item: canonical },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to install DeepSeek Harness",
    totalTime: "PT10M",
    step: [
      { "@type": "HowToStep", name: "Install Node.js", text: "Install a current Node.js runtime on your machine." },
      {
        "@type": "HowToStep",
        name: "Start the Web UI",
        text: "Run `npx @deepseek-ai/dsh web`. The Web UI is served at http://127.0.0.1:3080 by default.",
      },
      {
        "@type": "HowToStep",
        name: "Connect a model provider",
        text: "Open Settings → Models and add an OpenAI-compatible provider: DeepSeek, GLM, Kimi, Qwen, local Ollama or an Anthropic key.",
      },
      {
        "@type": "HowToStep",
        name: "Pick an agent mode",
        text: "Choose Standard, Code, Minimal or Creator mode, then start prompting.",
      },
    ],
  };

  return (
    <>
      <Head>
        <title>DeepSeek Harness: Open-Source Claude Code Alternative</title>
        <meta
          name="description"
          content="DeepSeek Harness is a free, MIT-licensed coding agent where every model, tool and the UI is a plugin. Install with npx @deepseek-ai/dsh web, swap models, and see how it really compares to Claude Code."
        />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta
          property="og:title"
          content="DeepSeek Harness Explained: The Plugin-First Claude Code Alternative"
        />
        <meta
          property="og:description"
          content="Everything is a plugin — models, tools, sessions, even the UI. What DeepSeek Harness is, how Cordis works, how it compares to Claude Code, and where the limits still are."
        />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="DeepSeek Harness: Open-Source Claude Code Alternative"
        />
        <meta
          name="twitter:description"
          content="A free, MIT-licensed, plugin-first coding agent runtime built on Cordis. Setup, architecture, Claude Code comparison and honest limitations."
        />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <meta property="article:published_time" content={PUBLISHED} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
      </Head>

      <div className="min-h-screen bg-white">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/blog")}
            className="mb-6 text-slate-600 hover:text-[#0066FF]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>

          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-[#0066FF]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-[#0066FF]">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-700">DeepSeek Harness explained</span>
          </nav>

          <div className="mb-6 inline-flex items-center gap-2 text-xs font-medium bg-blue-50 text-[#0066FF] border border-blue-200 px-3 py-1.5 rounded-full">
            <Boxes className="h-3.5 w-3.5" />
            Coding agents · August 16, 2026 · Independent Guide
          </div>

          <article>
            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
              DeepSeek Harness Explained: The Open-Source, Plugin-First Alternative to Claude Code
            </h1>

            <p className="text-lg text-slate-600 mb-2">
              By the Deep Seek AI editorial desk · August 16, 2026 · 9 min read
            </p>
            <LastUpdated date="2026-08-16" className="mb-6" />

            <p className="text-base text-slate-500 mb-8 italic">
              <strong>In short:</strong> DeepSeek Harness is a free, MIT-licensed, plugin-first
              coding agent built on the <strong>Cordis</strong> runtime — an open-source alternative
              to Claude Code where every model, tool and even the UI is hot-swappable.
            </p>

            <p className="text-slate-700 mb-4">
              For most of 2025, "coding agents" meant a small handful of closed products — Claude
              Code, Codex, Cursor — where the harness around the model (tools, UI, slash commands,
              MCP glue) was a black box you rented rather than owned. DeepSeek Harness is the first
              credible attempt to blow that model apart: an open-source, plugin-first coding agent
              runtime in which the underlying language model is just another component you can swap.
              If you have been searching for an <strong>open-source Claude Code alternative</strong>,
              this is the project reshaping that conversation.
            </p>

            <Card className="mb-8 border-blue-200 bg-blue-50/60">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3 mt-0">The 60-second version</h2>
                <ul className="text-sm text-slate-800 space-y-2 list-disc pl-5">
                  <li>
                    <strong>What it is:</strong> a coding agent runtime from DeepSeek — repo{" "}
                    <code>deepseek-harness</code>, CLI <code>dsh</code> — in developer preview.
                  </li>
                  <li>
                    <strong>Install:</strong> Node.js, then <code>npx @deepseek-ai/dsh web</code>;
                    the Web UI opens at <code>http://127.0.0.1:3080</code>.
                  </li>
                  <li>
                    <strong>Architecture:</strong> everything is a plugin — models, tools, skills,
                    sessions, sandboxes, storage, loops, scheduling and the UI.
                  </li>
                  <li>
                    <strong>Model-agnostic:</strong> any OpenAI-compatible provider, including
                    Anthropic keys.
                  </li>
                  <li>
                    <strong>Caveat:</strong> developer preview, breaking changes guaranteed, plugin
                    supply-chain risk, and no independent SWE-bench comparisons yet.
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              What DeepSeek Harness actually is
            </h2>
            <p className="text-slate-700 mb-4">
              DeepSeek Harness is a coding agent runtime released by DeepSeek in developer preview.
              It uses an architecture where <em>everything is a plugin</em>, and it is powered by
              Cordis, whose design is described in the paper "A Programming Paradigm for
              Spatiotemporal Composability."
            </p>
            <p className="text-slate-700 mb-4">
              You install it with a single command and it launches a local web app. After installing
              Node.js, running the command below starts the Web UI, served at{" "}
              <code>http://127.0.0.1:3080</code> by default. From there you point it at an API
              provider, pick an agent mode and start prompting.
            </p>
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm mb-4">
              <code>{`npx @deepseek-ai/dsh web
# Web UI → http://127.0.0.1:3080`}</code>
            </pre>
            <p className="text-slate-700 mb-6">
              Two things are worth flagging up front. First, DeepSeek Harness is currently in{" "}
              <strong>developer preview</strong> and iterating rapidly — there will be
              compatibility-breaking changes. Second, it is not just a terminal wrapper: the web UI,
              the slash commands, the model connectors and even the agent loop itself are separate
              plugins mounted into a shared runtime.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              The "harness" idea — why it matters
            </h2>
            <p className="text-slate-700 mb-4">
              An AI coding assistant has exactly one "brain" (the model). Everything else — context
              assembly, tool calls, MCP servers, the UI, the memory — is the harness. Claude Code is
              a harness. Codex is a harness. The model is interchangeable in principle, but in most
              products the harness is hard-coded against a specific provider.
            </p>
            <blockquote className="border-l-4 border-[#0066FF] pl-4 italic text-slate-700 mb-6">
              "The model is the soul of an agent. A harness lets an agent understand its environment,
              use tools, and keep working in real-world settings."
            </blockquote>
            <p className="text-slate-700 mb-6">
              The DeepSeek Harness bet is that the harness — not the model — is where the interesting
              engineering work now lives, and that it should be open and composable.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Everything is a plugin: the Cordis architecture
            </h2>
            <p className="text-slate-700 mb-4">
              The load-bearing claim of the whole project is that <em>every</em> capability is a
              plugin — not "many things are plugins." According to the official docs:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-6">
              <li>The Cordis kernel manages plugin mounting, unmounting and dependencies.</li>
              <li>
                Plugins provide every agent capability: models, tools, skills, sessions, sandboxes,
                storage, loops, scheduling and the UI.
              </li>
              <li>Cordis services and events let plugins work together.</li>
              <li>
                Developers can select, swap or extend any capability in configuration — without
                changing the DeepSeek Harness source code.
              </li>
            </ul>
            <p className="text-slate-700 mb-6">
              Underneath, Cordis is a small dependency-injection-style runtime. A plugin is an object
              implementing a <code>Service</code>: either a function with optional <code>inject</code>{" "}
              and <code>apply(ctx)</code> fields, or a <code>Service</code> subclass whose lifecycle
              Cordis mounts into the current context. A context is a repository of services, and a
              service claims a stable key such as <code>ctx.tools</code>, <code>ctx.llm</code> or{" "}
              <code>ctx.sessions</code> — other plugins find services by key instead of importing a
              concrete implementation. If that sounds like React's declarative model or Kubernetes
              reconciliation loops applied to agents, that is the right instinct.
            </p>

            <h3 className="text-xl font-semibold text-slate-900 mt-8 mb-2">
              Revertible effects and reactive coeffects
            </h3>
            <p className="text-slate-700 mb-4">
              The paper behind Cordis argues for two properties every runtime component should have:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li>
                <strong>Revertible effects.</strong> When a component mutates the shared environment
                (a file handle, a session, a database connection), the runtime records the operation
                needed to undo it. Removing the component cleans up automatically — no lingering
                event handlers or stale state.
              </li>
              <li>
                <strong>Reactive coeffects.</strong> Components declaratively state what they need. A
                plugin that declares a database dependency is activated when that service appears and
                gracefully deactivated when it goes away — no hardcoded "plan B" in every consumer.
              </li>
            </ul>
            <p className="text-slate-700 mb-6">
              Combined, these yield the three practical properties Cordis emphasises: automatic
              cleanup, continuous dependency reaction, and live reconfiguration with hot module
              replacement.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              DeepSeek Harness vs Claude Code
            </h2>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-slate-200">
                <caption className="sr-only">
                  Comparison of DeepSeek Harness and Claude Code across four dimensions
                </caption>
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 border-b border-slate-200 font-semibold">Dimension</th>
                    <th className="text-left p-3 border-b border-slate-200 font-semibold">Claude Code</th>
                    <th className="text-left p-3 border-b border-slate-200 font-semibold">DeepSeek Harness</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row) => (
                    <tr key={row.dimension} className="border-b border-slate-100 align-top">
                      <td className="p-3 font-semibold text-slate-900">{row.dimension}</td>
                      <td className="p-3 text-slate-700">{row.claude}</td>
                      <td className="p-3 text-slate-700">{row.harness}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-slate-700 mb-6">
              The session-durability demo is worth treating carefully: a presenter clicking
              "continue" after a mid-session crash and resuming with full context is a live
              demonstration of the revertible-effects idea — not a proof that it holds under every
              failure mode.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              The four agent modes
            </h2>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3 border-b border-slate-200 font-semibold">Mode</th>
                    <th className="text-left p-3 border-b border-slate-200 font-semibold">What it gives you</th>
                  </tr>
                </thead>
                <tbody>
                  {modes.map((m) => (
                    <tr key={m.mode} className="border-b border-slate-100 align-top">
                      <td className="p-3 font-mono text-[#0066FF] whitespace-nowrap">{m.mode}</td>
                      <td className="p-3 text-slate-700">{m.what}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              The ecosystem: real, but young
            </h2>
            <p className="text-slate-700 mb-4">
              An "everything is a plugin" architecture only matters if there are plugins. A community
              registry has already formed. Real examples:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              {plugins.map((p) => (
                <li key={p.name}>
                  <code>{p.name}</code> — {p.what}
                </li>
              ))}
            </ul>
            <p className="text-slate-700 mb-6">
              That last one is telling: the community is already treating DeepSeek Harness as one node
              in a multi-agent workflow rather than a replacement for everything else.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              The cost story has shifted
            </h2>
            <p className="text-slate-700 mb-4">
              A common reason people looked at DeepSeek early in 2025 was price. That story is more
              nuanced now. DeepSeek V3.2 scores 66 on the Artificial Analysis Intelligence Index — a
              substantial uplift over V3.2-Exp — and DeepSeek switched its main API endpoint to V3.2
              with no pricing change from V3.2-Exp, putting it at <strong>$0.28 / $0.42</strong> per
              1M input/output tokens, with <strong>90% off cached input tokens</strong>. It is also
              efficient on token usage: $54 to run the Artificial Analysis test suite, versus $380 for
              DeepSeek-R1 0528, $380 for Kimi K2 Thinking, $859 for GPT-5.1 High and $1,201 for
              Gemini 3 Pro.
            </p>
            <Card className="mb-6 border-amber-200 bg-amber-50">
              <CardContent className="p-5 flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900 m-0">
                  Newer DeepSeek Pro-tier variants carry noticeably higher per-task costs than the
                  earlier "practically free" versions. If you are switching purely for price, re-run
                  your own math against the{" "}
                  <Link to="/pricing" className="underline font-semibold">
                    current DeepSeek rate card
                  </Link>
                  .
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Honest limitations
            </h2>
            <p className="text-slate-700 mb-4">
              One reviewer, who has spent six months building a sophisticated AI-body-signals mobile
              app as a personal benchmark, tried to reproduce a minimal version of it in DeepSeek
              Harness using DeepSeek's Pro model. His verdict: better than Kimi, GLM and Qwen on the
              same prompt, but visibly weaker than Claude Opus — nervous-system rendering was off and
              animation quality lagged the Anthropic version.
            </p>
            <p className="text-slate-700 mb-4">
              That is the key point for buyers: <strong>harness architecture is separate from model
              quality</strong>. DeepSeek Harness lets you attach <em>any</em> model — including Claude
              Opus, if you have an API key — so architectural flexibility and generation quality are
              decoupled decisions.
            </p>
            <Card className="mb-6 border-slate-200 bg-slate-50">
              <CardContent className="p-5">
                <h3 className="text-base font-bold text-slate-900 mb-3 mt-0 flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-slate-600" />
                  Three caveats before you standardise on it
                </h3>
                <ul className="text-sm text-slate-800 space-y-2 list-disc pl-5">
                  <li>Developer-preview status means breaking changes are guaranteed.</li>
                  <li>
                    "Everything is a plugin" has a supply-chain implication: a malicious community
                    plugin has the same rights as a first-party one.
                  </li>
                  <li>
                    There are no independent SWE-bench-style comparisons yet against Claude Code,
                    Codex or Cursor.
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Who should try it
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-6">
              <li>
                <strong>Teams that want to own their agent stack.</strong> If the black-box harness is
                the thing blocking you, this is the first serious escape hatch.
              </li>
              <li>
                <strong>Multi-model shops.</strong> Routing cheap turns to DeepSeek V3.2 and hard
                turns to a frontier model is a configuration change, not a rewrite.
              </li>
              <li>
                <strong>Agent researchers.</strong> Creator mode plus the Trajectory panel make the
                loop itself inspectable.
              </li>
              <li>
                <strong>Not yet:</strong> teams that need a stable, supported tool with published
                benchmark parity today.
              </li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="mb-10">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-slate-900 font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-700">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <Card className="mb-10 border-blue-200 bg-blue-50/60">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-2 mt-0 flex items-center gap-2">
                  <Terminal className="h-4 w-4 text-[#0066FF]" />
                  Keep reading
                </h2>
                <ul className="text-sm space-y-2 list-disc pl-5 text-slate-800">
                  <li>
                    <Link to="/blog/deepseek-reasonix-coding-agent-cli" className="text-[#0066FF] underline">
                      Reasonix: the DeepSeek-native terminal coding agent
                    </Link>
                  </li>
                  <li>
                    <Link to="/deepseek-api" className="text-[#0066FF] underline">
                      The DeepSeek API explained
                    </Link>
                  </li>
                  <li>
                    <Link to="/pricing" className="text-[#0066FF] underline">
                      Current DeepSeek pricing and cost calculator
                    </Link>
                  </li>
                  <li>
                    <Link to="/deepseek-vs-claude" className="text-[#0066FF] underline">
                      DeepSeek vs Claude: model-level comparison
                    </Link>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <p className="text-xs text-slate-500 mb-8">
              This is an independent, fan-run guide. It is not affiliated with, endorsed by or
              operated by DeepSeek. Figures reflect publicly documented sources at the time of
              writing; always verify against the official documentation before committing spend.
            </p>

            <RelatedLinks />
            <NavigationButtons />
          </article>
        </main>
      </div>
    </>
  );
};

export default DeepseekHarness;
